import { useState, useCallback } from "react";
import { useCustomMutation } from "@refinedev/core";
import { CREATE_SOURCING_MUTATION } from "../queries";
import { SourcingFormData, SourcingType, SourcingScope, InvitationMode, VisibleToSuppliers, TechnicalEvaluationMode, EvaluationCriteriaType } from "../interface";

export const useCreateSourcing = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<SourcingFormData>({
    prIds: [],
    title: "",
    description: "",
    type: SourcingType.RFQ,
    sourcingScope: SourcingScope.NCB,
    invitationMode: InvitationMode.CLOSED,
    items: [],
    totalAmount: 0,
    currency: "ETB",
    notes: "",
    attachments: [],
    evaluationCriterias: [],
    technicalEvaluationMode: TechnicalEvaluationMode.NONE,
    approvalType: 'EXTERNAL',
    steps: [],
    externalWorkflow: null,
  });

  const { mutate: createSourcingMutation, isLoading: loading } = useCustomMutation();

  const updateFormData = useCallback((updates: Partial<SourcingFormData>) => {
    setFormData(prev => {
      const newData = { ...prev, ...updates };

      // If type is being changed to RFP, set technicalEvaluationMode to MATRIX and threshold to 80
      if (updates.type === SourcingType.RFP) {
        newData.technicalEvaluationMode = TechnicalEvaluationMode.MATRIX;
        newData.technicalEvaluationThreshold = 80; // Default 80% for RFP
        // If no evaluation criteria exist, add defaults
        if (!prev.evaluationCriterias || prev.evaluationCriterias.length === 0) {
          newData.evaluationCriterias = [
            {
              id: 'default-1',
              name: 'Technical Compliance',
              description: 'Conformance to technical specifications',
              weight: 30,
              visibleToSuppliers: VisibleToSuppliers.FULL,
              type: EvaluationCriteriaType.TECHNICAL,
            },
            {
              id: 'default-2',
              name: 'Experience & References',
              description: 'Relevant experience and references',
              weight: 20,
              visibleToSuppliers: VisibleToSuppliers.FULL,
              type: EvaluationCriteriaType.TECHNICAL,
            },
            {
              id: 'default-3',
              name: 'Delivery Time',
              description: 'Proposed delivery timeline',
              weight: 10,
              visibleToSuppliers: VisibleToSuppliers.FULL,
              type: EvaluationCriteriaType.TECHNICAL,
            },
            {
              id: 'default-4',
              name: 'Financial (Price vs Est.)',
              description: 'Price competitiveness against estimate',
              weight: 40,
              visibleToSuppliers: VisibleToSuppliers.FULL,
              type: EvaluationCriteriaType.FINANCIAL,
            },
          ];
        }
      }

      // If type is RFQ and technicalEvaluationMode is being changed to MATRIX, set threshold to 60 and add default criteria
      if (newData.type === SourcingType.RFQ && updates.technicalEvaluationMode === TechnicalEvaluationMode.MATRIX) {
        newData.technicalEvaluationThreshold = 60; // Default 60% for RFQ
        // If no evaluation criteria exist, add defaults for RFQ
        if (!prev.evaluationCriterias || prev.evaluationCriterias.length === 0) {
          newData.evaluationCriterias = [
            {
              id: 'default-1',
              name: 'Technical Compliance',
              description: 'Conformance to technical specifications',
              weight: 40,
              visibleToSuppliers: VisibleToSuppliers.FULL,
              type: EvaluationCriteriaType.TECHNICAL,
            },
            {
              id: 'default-2',
              name: 'Delivery Time',
              description: 'Proposed delivery timeline',
              weight: 20,
              visibleToSuppliers: VisibleToSuppliers.FULL,
              type: EvaluationCriteriaType.TECHNICAL,
            },
            {
              id: 'default-3',
              name: 'Price Competitiveness',
              description: 'Price competitiveness against market rates',
              weight: 40,
              visibleToSuppliers: VisibleToSuppliers.FULL,
              type: EvaluationCriteriaType.FINANCIAL,
            },
          ];
        }
      }

      return newData;
    });
  }, []);

  const updateBidDetails = useCallback((bidDetails: { items: any[], totalAmount: number, currency?: string, notes?: string, title?: string, description?: string }) => {
    setFormData(prev => ({ ...prev, ...bidDetails }));
  }, []);

  const nextStep = useCallback(() => {
    setCurrentStep(prev => Math.min(prev + 1, 3));
  }, []);

  const prevStep = useCallback(() => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  }, []);

  const goToStep = useCallback((step: number) => {
    setCurrentStep(Math.max(0, Math.min(step, 3)));
  }, []);

  const reset = useCallback(() => {
    setCurrentStep(0);
    setFormData({
      prIds: [],
      title: "",
      description: "",
      type: SourcingType.RFQ,
      sourcingScope: SourcingScope.NCB,
      invitationMode: InvitationMode.CLOSED,
      items: [],
      totalAmount: 0,
      currency: "ETB",
      notes: "",
      attachments: [],
      evaluationCriterias: [],
      technicalEvaluationMode: TechnicalEvaluationMode.NONE,
      approvalType: 'EXTERNAL',
      steps: [],
      externalWorkflow: null,
    });
  }, []);

  const submit = useCallback(async () => {

      const input = {
        prIds: formData.prIds,
        title: formData.title,
        description: formData.description,
        type: formData.type,
        sourcingScope: formData.sourcingScope,
        invitationMode: formData.invitationMode,
        items: formData.items.map(item => {
          const { id, ...rest } = item;
          return rest;
        }),
        totalAmount: formData.totalAmount,
        currency: formData.currency,
        notes: formData.notes,
        attachments: formData.attachments,
        evaluationCriterias: formData.evaluationCriterias?.map(criteria => {
          const { id, ...rest } = criteria;
          return rest;
        }),
        technicalEvaluationMode: formData.technicalEvaluationMode,
        technicalEvaluationThreshold: formData.technicalEvaluationThreshold,
        approvalType: formData.approvalType,
        steps: formData.steps,
        externalWorkflow: formData.externalWorkflow,
      };

      console.log('Sourcing input:', input);
      const result = await createSourcingMutation({
        meta: {
          gqlMutation: CREATE_SOURCING_MUTATION,
        },
        values: { input },
        url: "",
        method: "post"
      });

      // Handle Refine's response structure - the actual data is nested
      return (result as any)?.data?.createSourcing || result;
    } 
  , [formData, createSourcingMutation]);

  const isStepValid = useCallback((step: number) => {
    switch (step) {
      case 0: // Bid details
        return formData.items.length > 0;
      case 1: // Type & Scope selection
        return !!formData.type && !!formData.sourcingScope;
      case 2: // Attachments (optional)
        return true; // Attachments are optional
      case 3: // Review and approval
        if (!formData.approvalType) return false;
        if (formData.approvalType === 'EXTERNAL') {
          return !!formData.externalWorkflow;
        } else if (formData.approvalType === 'LOCAL') {
          return formData.steps && formData.steps.length > 0;
        }
        return false;
      default:
        return false;
    }
  }, [formData]);

  const canProceedToNext = isStepValid(currentStep);
  const canGoBack = currentStep > 0;

  return {
    currentStep,
    formData,
    loading,
    updateFormData,
    updateBidDetails,
    nextStep,
    prevStep,
    goToStep,
    reset,
    submit,
    isStepValid,
    canProceedToNext,
    canGoBack,
  };
};